/**
 * BrandedRefreshControl — drop-in replacement for RN's RefreshControl.
 *
 * Pull-to-refresh is one of the very first interactions a mobile user
 * makes on any list-driven screen. Stock iOS shows a thin gray spinner;
 * stock Android shows a gray ring on a system-default background. Both
 * read as "generic React Native app" — exactly the impression we don't
 * want for the first touch.
 *
 * This wrapper applies Tago brand defaults (coral spinner + theme-aware
 * surface background on Android, coral tint on iOS) so every list screen
 * gets the same branded refresh feel for free. Callers can still
 * override any underlying RefreshControl prop if needed.
 *
 * Why a wrapper rather than a component-level override:
 *   - RefreshControl is platform-native. Wrapping its UI with a Lottie /
 *     SVG overlay is technically possible but fights the native gesture
 *     and leads to dropped frames when the user pulls hard. The native
 *     behaviour is better than anything we can build on top, so we just
 *     theme it correctly.
 *   - Theme-aware on Android: progressBackgroundColor pulls from the
 *     resolved surface color so the spinner sits on a card-tinted
 *     background, not the default white. That makes dark mode work
 *     without a second component.
 */
import React from 'react'
import { Platform, RefreshControl, type RefreshControlProps } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { COLORS, SPACING } from '../../lib/brand'
import { useSemanticColors } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'

type BrandedRefreshControlProps = Omit<RefreshControlProps, 'tintColor' | 'colors'> & {
  /** iOS-only override. Defaults to coral. */
  tintColor?: string
  /** Android-only override. Defaults to [coral]. */
  colors?: string[]
}

export function BrandedRefreshControl({
  tintColor,
  colors: overrideColors,
  progressBackgroundColor: overrideBg,
  progressViewOffset: overrideOffset,
  ...props
}: BrandedRefreshControlProps) {
  const { isDark } = useTheme()
  const semantic = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()

  if (Platform.OS === 'ios') {
    return <RefreshControl tintColor={tintColor ?? COLORS.coral.DEFAULT} {...props} />
  }

  // Android. The app is edge-to-edge (targetSdk 36), so the native
  // SwipeRefreshLayout paints its indicator at the very top of the scroll view —
  // under the status bar — and the default white progress circle reads as a chip
  // floating over the header. Push it below the top inset and blend the circle
  // with the page so only the coral arc shows.
  return (
    <RefreshControl
      colors={overrideColors ?? [COLORS.coral.DEFAULT]}
      progressBackgroundColor={overrideBg ?? semantic.background}
      progressViewOffset={overrideOffset ?? insets.top + SPACING.sm}
      tintColor={tintColor}
      {...props}
    />
  )
}
